/**
 * Rule-chat API client — /api/v1/admin/rules/chat (B-623).
 *
 * Conversational query/author over the companion-rule corpus. Reuses the same
 * Firebase auth interceptor pattern as triageApi.ts.
 */
import axios from 'axios';
import { auth } from './firebase';

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://est.anterotrail.com/api/v1';

const client = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    try {
      config.headers.Authorization = `Bearer ${await user.getIdToken()}`;
    } catch (err) {
      console.error('[ruleChatApi] could not get firebase token', err);
    }
  } else if (
    import.meta.env.DEV &&
    import.meta.env.VITE_DEV_AUTH_BYPASS === 'true'
  ) {
    config.headers.Authorization = 'Bearer dev-bypass-token';
  }
  return config;
});

client.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const message =
      error?.response?.data?.detail ||
      error?.response?.data?.message ||
      error?.message ||
      'Rule chat request failed';
    return Promise.reject(new Error(message));
  },
);

export interface CodeCandidate {
  code: string;
  cat: string;
  sel: string;
  dsc: string | null;
}

export interface ResolvedTerm {
  description: string;
  chosen: CodeCandidate | null;
  candidates: CodeCandidate[];
}

export interface RuleMatch {
  id: string;
  name: string;
  status: string;
  is_active: boolean;
  verdict: string | null;
  severity: string | null;
}

export type ChatStatus =
  | 'needs_clarification'
  | 'exists_active'
  | 'exists_proposed'
  | 'not_found';

export interface ChatVerdict {
  status: ChatStatus;
  message: string;
  intent: string;
  antecedent: ResolvedTerm | null;
  consequent: ResolvedTerm | null;
  guard_unless: ResolvedTerm | null;
  guard_only_if: ResolvedTerm | null;
  existing_rules: RuleMatch[];
  drafted_rule: Record<string, unknown> | null;
}

export const ruleChatApi = {
  ask: (message: string): Promise<ChatVerdict> =>
    client.post('/admin/rules/chat', { message }) as unknown as Promise<ChatVerdict>,

  saveDraft: (
    rule: Record<string, unknown>,
  ): Promise<{ id: string; name: string; status: string; saved: boolean }> =>
    client.post('/admin/rules/chat/save', { rule }) as unknown as Promise<{
      id: string;
      name: string;
      status: string;
      saved: boolean;
    }>,
};
